import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { rangeFromWidth } from './content-area';
import { ContentAreaContext } from './content-area-context';
import styles from "./content-area.module.css";

class ResizeObserverNull {
  constructor() {
    this.disconnect = () => null;

    this.observe = () => null;

    this.unobserve = () => null;
  }

}
/** Internal hook to fetch the container area range from the width of the content area. */


const useContentAreaResizeObserver = containerRef => {
  let animationFrameRequest;
  const [range, setRange] = useState(() => 'xxxs'); // The range produced by this hook is ready to be used if the component is
  // to render immediately or if the range is derived from a hint from the
  // container.

  const [isRangeKnown, setIsRangeKnown] = useState(false);
  const [resizeObserver] = useState(() => {
    const Observer = typeof ResizeObserver !== 'undefined' ? ResizeObserver : ResizeObserverNull;
    return new Observer(entries => {
      // Any additional resize events resulting from this update will fire after
      // requestAnimationFrame is resolved. This is useful to avoid errors within
      // the ResizeObserver event loop introduced by components outside of our control
      // within the layout:
      // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_error
      animationFrameRequest = requestAnimationFrame(() => {
        for (const entry of entries) {
          if (entry.contentBoxSize) {
            const inlineSize = entry.contentBoxSize[0].inlineSize;
            setRange(rangeFromWidth(inlineSize));
            setIsRangeKnown(true);
          }
        }
      });
    });
  });
  useEffect(() => {
    const containerRefNode = containerRef.current;

    if (containerRefNode) {
      resizeObserver.observe(containerRefNode);
    }

    return () => {
      if (containerRefNode) {
        resizeObserver.unobserve(containerRefNode);
      }

      if (animationFrameRequest) {
        cancelAnimationFrame(animationFrameRequest);
      }
    };
  });
  return {
    range,
    isRangeKnown
  };
};
/**
 * Props for `ContentArea`
 */


/**
 * ### ContentArea
 *
 * The root of any layout content area. Renders the `div` that represents
 * the container against which all container queries are checked.
 */
export const ContentArea = _ref => {
  let {
    className,
    children
  } = _ref;
  const containerRef = useRef(null);
  const contextValue = useContentAreaResizeObserver(containerRef);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: classNames(styles.container, className)
  }, /*#__PURE__*/React.createElement(ContentAreaContext.Provider, {
    value: contextValue
  }, children));
};
//# sourceMappingURL=content-area.react-component.js.map